import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import { useForm } from 'react-hook-form';
import {
    Button,
    Card,
    CardBody,
    CardError,
    CardFooter,
    CardHeader,
    TextInput,
    Paragraph
} from 'view/components/common';
import { 
    isFormValid,
    isRequired, 
    isChained, 
    isEmail,
    Data
} from 'core/utilities';
import { forgotPassword } from 'core/context';
import { useKeypress } from 'view/hooks';
import { Spinner } from 'view/components/layout';

export const ForgotPasswordPage : React.FunctionComponent = () => 
{
    const history = useHistory();
    const [ submitError, setSubmitError ] = useState<string | undefined>();
    const [ isLoading,   setIsLoading   ] = useState<boolean>(false);
    const [ submitted,   setSubmitted   ] = useState<boolean>(false);
    const [ formErrors,  setFormErrors  ] = useState<Data>({});
    const [ canContinue, setCanContinue ] = useState<boolean>(false);
    const { register, setValue, getValues } = useForm({ mode : 'onChange' });

    const onBack = () => history.push('/login');
    const onSubmit = async () => 
    {
        if (canContinue && !isLoading)
        {
            setIsLoading(true);
            setCanContinue(false);
    
            const payload = getValues() as { email : string };
            const { error } = await forgotPassword(payload);

            setIsLoading(false);
            setCanContinue(true);
    
            if (error)
            {
                setSubmitError(error);
            }
            else 
            {
                setSubmitError(undefined);
                setSubmitted(true);
            }
        }
    };

    useEffect(() => 
    {   // Continuously revalidate form values
        setCanContinue(isFormValid(formErrors, [ 'email' ]));
    }, [ formErrors, setCanContinue ]);

    useKeypress('Enter', onSubmit);

    return (
        <Container>
            <Card height={420}>
                <CardHeader 
                    content='Forgot Password'
                    css='margin-top: 16px; padding-bottom: 20px;'
                    step=' '
                    backHandler={onBack}
                />
                {submitError && (
                    <CardError
                        content={submitError}
                        css={'margin-bottom: -5px; margin-top: -10px;'}
                    />
                )}
                <CardBody>
                    {isLoading ? (
                        <Spinner css='height: inherit; margin-top: 50px;' />
                    ) : submitted ? (
                        <Paragraph> 
                            Sent! You should receive an email shortly with instructions to reset Your password. Remember to check your junk folder if you have trouble finding it.
                        </Paragraph>
                    ) : (
                        <TextInput
                            type='text'
                            name='email'
                            label='Enter the email you signed up with and we’ll send you instructions to reset your password'
                            placeholder='salaam@manzil.ca'
                            validator={isChained([ isEmail, isRequired ])}
                            setValue={setValue}
                            setFormErrors={setFormErrors}
                            input={register('email')}
                        />
                    )}
                </CardBody>
                <CardFooter>
                    {submitted ? (
                        <Button
                            title='Done'
                            type='button'
                            onClick={onBack}
                            css='text-transform: none; padding-bottom: 3px;'
                            arrowCss='display: none;'
                        />
                    ) : (
                        <Button
                            title='Send'
                            type='button'
                            disabled={!canContinue || isLoading}
                            onClick={onSubmit}
                            css='text-transform: none; padding-bottom: 3px;'
                            arrowCss='padding-left: 5px; margin-top: 5px;'
                        />
                    )}
                </CardFooter>
            </Card>
        </Container>
    );
};

const Container = styled.div`
    position: relative;
`;